import FieldDrawer, { type GroupTarget } from '@components/Customization/PoiTypes/FieldDrawer';
import GroupDrawer from '@components/Customization/PoiTypes/GroupDrawer';
import PoiTypeDrawer, { type PoiTypeDraft } from '@components/Customization/PoiTypes/PoiTypeDrawer';
import PoiTypeSchema from '@components/Customization/PoiTypes/PoiTypeSchema';
import PoiTypeDesigner from '@components/Customization/PoiDesigner/PoiTypeDesigner';
import ViewSwitch, { type PoiTypeView } from '@components/Customization/PoiDesigner/ViewSwitch';
import ShareDrawer from '@components/Customization/PoiTypes/ShareDrawer';
import DeleteConfirmationDialog from '@components/Shared/Dialog/DeleteConfirmationDialog';
import Header from '@components/Shared/Layout/Header';
import { arrayMove } from '@dnd-kit/sortable';
import { ROUTE_PATHS } from '@constants/routes';
import { candidatesOf, stepStatusCandidate } from '@lib/customization/poiDraft';
import { pruneRules, rulesReferencing } from '@lib/customization/poiRules';
import { dropDraftsNotListed, keptDraftsOf } from '@lib/customization/unsavedWork';
import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useLocalization } from '@lib/localization';
import { has } from '@lib/permission';
import { Box, Button, IconButton, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { backButtonStyles, dangerHoverStyles, detailIconStyles } from '@pages/CustomPoiTypes.styles';
import {
  resolveLabel, toPoiTypeBody, useAddPoiTypeMutation, useDeletePoiTypeMutation, useDeletePoiTypeShareMutation,
  useGetEffectivePoiTypeQuery, useGetPoiTypesQuery, useSharePoiTypeMutation, useUpdatePoiTypeMutation,
  type PoiField, type PoiFieldGroup, type PoiType, type PoiTypeBody,
} from '@store/api/poiTypeEndpoints';
import { useGetTeamDirectoryQuery } from '@store/api/teamEndpoints';
import { apiErrorMessage, isServerError } from '@store/error';
import { usePermission } from '@store/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AngleLeftIcon, CopyIcon, EditIcon, MapMarkerIcon, ShareAltIcon, TrashIcon,
} from 'react-line-awesome';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

/** Which deletion is waiting on the confirmation dialog. */
type PendingDelete =
  | { kind: 'field'; group: GroupTarget; index: number }
  | { kind: 'group'; index: number }
  | { kind: 'type' }
  | null;

type DrawerState =
  | { kind: 'none' }
  | { kind: 'type' }
  | { kind: 'group'; index: number | null }
  | { kind: 'field'; group: GroupTarget; index: number | null }
  | { kind: 'share' };

/**
 * One POI type and everything that can be done to it.
 *
 * The list stays a list — this page owns every mutation, the same split the teams pages
 * use. Reading the whole catalogue to find one type is deliberate: it is the query the
 * list already holds, so arriving here from a row costs no extra request.
 */
const PoiTypeDetailPage = () => {
  const { t } = useLocalization();
  const locales = useCustomizationLocales();
  const navigate = useNavigate();
  const { poiTypeId } = useParams<{ poiTypeId: string }>();
  const { canInAnyTeam, userId } = usePermission();

  // The designer and the JSON edit one draft; the field table edits the saved type directly.
  const [view, setView] = useState<PoiTypeView>('designer');
  const [trying, setTrying] = useState(false);

  const { data, isLoading, fulfilledTimeStamp } = useGetPoiTypesQuery();
  const poiTypes = useMemo(() => data ?? [], [data]);

  // A type somebody else deleted, or one that is no longer the reader's, turns into the "not
  // found" below with the catalogue's next read, and the designer that kept the reader's
  // draft of it goes with it. Nothing is left to discard that draft in, so it is given up
  // here as it is in the list: by a catalogue that was answered after the draft was last
  // kept (`dropDraftsNotListed`). When it was answered is enough, because the designer is
  // only ever shown for a type the catalogue at hand holds: a draft was kept under such a
  // catalogue, and one that came after and does not hold the type was read once it had gone.
  // So a change made while that read was on its way is given up with the rest.
  useEffect(() => {
    if (!data || fulfilledTimeStamp === undefined) return;

    dropDraftsNotListed('poi-type', userId, data.map(({ _id }) => _id), fulfilledTimeStamp);
  }, [userId, data, fulfilledTimeStamp]);

  const inherits = Boolean(poiTypes.find((candidate) => candidate._id === poiTypeId)?.inheritFrom);

  // Fetched for any type that inherits: a rule may point at an ancestor's field, so the rule
  // pickers, the designer and the outgoing prune all need the resolved schema. A type that
  // inherits nothing needs no resolving: everything it has is on the type itself.
  //
  // `currentData` rather than `data` for the same reason `PoiTypeSchema` uses it: `data`
  // holds the last result for any argument, so it can belong to a different type.
  const { currentData: resolved } = useGetEffectivePoiTypeQuery(
    poiTypeId ?? '', { skip: !poiTypeId || !inherits },
  );

  // The directory only feeds the share drawer's team picker, and asking for it needs
  // `folder:share` or `poi_type:post` — neither of which an INSPECTOR or ANALYST has, so
  // fetching it unconditionally met them with a 403 the moment they opened a type.
  const { data: teamDirectory = [] } = useGetTeamDirectoryQuery(undefined, {
    skip: !canInAnyTeam('poi_type:share'),
  });

  const [addPoiType, { isLoading: isCreating }] = useAddPoiTypeMutation();
  const [updatePoiType, { isLoading: isUpdating }] = useUpdatePoiTypeMutation();
  const [deletePoiType] = useDeletePoiTypeMutation();
  const [sharePoiType, { isLoading: isSharing }] = useSharePoiTypeMutation();
  const [deletePoiTypeShare, { isLoading: isRevoking }] = useDeletePoiTypeShareMutation();

  const [drawer, setDrawer] = useState<DrawerState>({ kind: 'none' });
  const [deleting, setDeleting] = useState<PendingDelete>(null);

  // A reorder has to show on the row the moment it is dropped, but the page renders from
  // the cached type and the refetch only lands after the PUT. Until it does, the order the
  // drop produced is held here and rendered in the cache's place.
  const [pendingOrder, setPendingOrder] = useState<Pick<PoiType, 'fields' | 'groups'> | null>(null);

  // What the page is showing right now, readable from inside a queued write.
  const shownRef = useRef<PoiType | undefined>(undefined);
  // One write at a time: each PUT claims the version it was made against, so two in flight
  // would make the second a 412 — which is what a run of quick drags would otherwise be.
  const saveChain = useRef<Promise<unknown>>(Promise.resolve());
  // The version the last accepted write produced. The server increments by exactly one, so
  // the next write in the queue can claim it without waiting for the refetch.
  const savedVersion = useRef(0);

  // Moving between types keeps this page mounted, so anything left open would carry the
  // group and field indices of the type we just left — and a pending delete would then
  // confirm against the wrong one.
  useEffect(() => {
    setDrawer({ kind: 'none' });
    setDeleting(null);
    setPendingOrder(null);
    savedVersion.current = 0;
  }, [poiTypeId]);

  /**
   * This type and everything below it in the inheritance tree — the types it must not be
   * offered as a parent, because inheriting from one of them is a cycle.
   *
   * The API refuses it (`assertInheritable`), but a refusal is all the user got: the picker
   * listed the descendant, and the 400 arrived only after the drawer had been filled in.
   *
   * Walked over the list the page already holds rather than asked for: a descendant the
   * reader cannot see is not in the picker to begin with.
   */
  const excludedParentIds = useMemo(() => {
    const excluded = new Set(poiTypeId ? [poiTypeId] : []);

    // Repeated sweeps rather than one pass: the list arrives in no particular order, so a
    // child can sit ahead of the parent that puts it out of bounds.
    let grew = true;

    while (grew) {
      grew = false;

      for (const candidate of poiTypes) {
        if (candidate.inheritFrom && excluded.has(candidate.inheritFrom) && !excluded.has(candidate._id)) {
          excluded.add(candidate._id);
          grew = true;
        }
      }
    }

    return excluded;
  }, [poiTypes, poiTypeId]);

  const poiType = poiTypes.find((candidate) => candidate._id === poiTypeId);

  // The refetch has landed once the cached version reaches what the last write produced;
  // from then on the cache *is* the new order and holding a copy of it would only go stale.
  useEffect(() => {
    if (poiType && poiType.version >= savedVersion.current) setPendingOrder(null);
  }, [poiType]);
  const close = () => setDrawer({ kind: 'none' });
  const backToList = () => navigate(ROUTE_PATHS.CUSTOMIZATIONS_POI_TYPES);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Header />
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  if (!poiType) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Header />
        <Box sx={{ width: '100%' }}>
          <Button onClick={backToList} startIcon={<AngleLeftIcon />} color="inherit" sx={backButtonStyles}>
            {t('Customization_BackToPoiTypes')}
          </Button>
          <Typography color="text.secondary">{t('Customization_PoiTypeNotFound')}</Typography>
        </Box>
      </Box>
    );
  }

  const canEdit = has(poiType, 'poi_type:put');
  const canDelete = has(poiType, 'poi_type:delete');
  const canShare = has(poiType, 'poi_type:share');
  const saving = isUpdating || isCreating;

  // Everything that reads content reads this one, so an edit made right after a drop
  // addresses the order on screen rather than the one the cache still holds.
  const shown: PoiType = pendingOrder ? { ...poiType, ...pendingOrder } : poiType;

  shownRef.current = shown;

  const parent = poiType.inheritFrom
    ? poiTypes.find((candidate) => candidate._id === poiType.inheritFrom)
    : undefined;

  const fieldCount = shown.fields.length
    + shown.groups.reduce((total, group) => total + group.fields.length, 0);

  // The resolve belongs to this type only while its id matches; after navigating, the
  // previous type's result is still what `currentData` holds for a moment.
  const effective = resolved?.id === poiType._id ? resolved : undefined;

  /** The fields a rule may point at, for the field and group drawers; see `candidatesOf`. */
  const ruleCandidates = candidatesOf(
    shown, (effective?.items ?? []).filter((entry) => entry.inheritedFrom), (text) => resolveLabel(text, locales), '',
    stepStatusCandidate(t),
  );

  /** Ancestors' fields by id, so the prune can tell a inherited reference from a dangling one. */
  const ancestorFields = new Map<string, PoiField>(
    (effective?.items ?? [])
      .filter((entry) => entry.inheritedFrom)
      .flatMap((entry) => (entry.kind === 'field' ? [entry.field] : entry.group.fields))
      .flatMap((field) => (field.id ? [[field.id, field] as const] : []))
  );

  /** Runs writes one after another; see `saveChain` for why they cannot overlap. */
  const enqueue = <T,>(run: () => Promise<T>): Promise<T> => {
    const result = saveChain.current.then(run, run);

    saveChain.current = result.then(() => undefined, () => undefined);

    return result;
  };

  /**
   * Every edit is a full replace — the API has no per-field endpoint, because `version`
   * can only answer "is my copy stale" if each edit lands as one write. The version read
   * is sent back, so an edit that landed in between is a 412 rather than a silent
   * overwrite.
   */
  /**
   * `claimed` is for an editor holding its own copy: the version that copy was taken from, so
   * an edit made elsewhere since is a 412 rather than overwritten. The drawers edit the page's
   * own copy, which is always the latest the page has seen.
   */
  const sendType = async (changes: Partial<PoiTypeBody>, claimed?: number) => {
    const source = shownRef.current!;
    const version = claimed ?? Math.max(source.version, savedVersion.current);

    const body: PoiTypeBody = {
      name: source.name,
      description: source.description,
      inheritFrom: source.inheritFrom,
      fields: source.fields,
      groups: source.groups,
      ...changes,
    };

    // A replace carries every rule, so this write is also where a deleted field, a retyped
    // one or a dropped option leaves conditions the server would reject — on this save and
    // on every unrelated one after it. Pruning here is the one place that sees all three.
    //
    // Unless the ancestors are not loaded: pruning against a chain we cannot see would
    // delete valid conditions, which is far worse than a 412 the user can retry.
    const ready = !source.inheritFrom || ancestorFields.size > 0 || Boolean(effective);

    const result = await updatePoiType({
      id: source._id,
      version,
      ...toPoiTypeBody(ready ? pruneRules(body, ancestorFields) : body),
    });

    if (!result.error) savedVersion.current = version + 1;

    return result;
  };

  const saveType = async (changes: Partial<PoiTypeBody>) => {
    const result = await enqueue(() => sendType(changes));

    if (!result.error) close();
  };

  /**
   * A drop shows at once and saves on its own. If the write is refused the optimistic copy
   * is dropped, which puts the rows back in the order the server still holds.
   */
  const reorder = (next: Pick<PoiType, 'fields' | 'groups'>) => {
    setPendingOrder(next);

    void enqueue(() => sendType(next)).then((result) => {
      if (result.error) setPendingOrder(null);
    });
  };

  const handleReorderField = (group: GroupTarget, from: number, to: number) => {
    const source = shownRef.current!;
    const fields = arrayMove(fieldsOf(group), from, to);

    reorder(group === 'd'
      ? { fields, groups: source.groups }
      : {
        fields: source.fields,
        groups: source.groups.map((current, index) => (index === group ? { ...current, fields } : current)),
      });
  };

  const handleReorderGroup = (from: number, to: number) => reorder({
    fields: shown.fields,
    groups: arrayMove(shown.groups, from, to),
  });

  const fieldsOf = (group: GroupTarget): PoiField[] =>
    group === 'd' ? shown.fields : shown.groups[group].fields;

  const withFields = (source: PoiType, group: GroupTarget, fields: PoiField[]): Partial<PoiTypeBody> =>
    group === 'd'
      ? { fields }
      : { groups: source.groups.map((current, index) => (index === group ? { ...current, fields } : current)) };

  const handleSaveField = async (field: PoiField, target: GroupTarget) => {
    if (drawer.kind !== 'field') return;

    const { group, index } = drawer;

    // Moving a field between groups is two edits to one document: drop it where it was,
    // append it where it goes. Both have to be in the single body we send.
    if (index !== null && target !== group) {
      const source = fieldsOf(group).filter((_, i) => i !== index);
      const afterRemoval = { ...shown, ...withFields(shown, group, source) } as PoiType;
      const targetFields = target === 'd' ? afterRemoval.fields : afterRemoval.groups[target].fields;

      await saveType({
        ...withFields(shown, group, source),
        ...withFields(afterRemoval, target, [...targetFields, field]),
      });
      return;
    }

    const current = fieldsOf(target);
    const next = index === null
      ? [...current, field]
      : current.map((existing, i) => (i === index ? field : existing));

    await saveType(withFields(shown, target, next));
  };

  const handleSaveGroup = async (group: PoiFieldGroup) => {
    if (drawer.kind !== 'group') return;

    const { index } = drawer;
    const groups = index === null
      ? [...shown.groups, group]
      : shown.groups.map((existing, i) => (i === index ? { ...existing, ...group } : existing));

    await saveType({ groups });
  };

  const handleSaveType = async (draft: PoiTypeDraft) => {
    await saveType({ name: draft.name, description: draft.description, inheritFrom: draft.inheritFrom });
  };

  /**
   * What a confirmed deletion will do, and what to say it is about.
   *
   * Held as one piece of state so the three deletions share the app's own confirmation
   * dialog instead of `window.confirm`, and so the dialog never has to work out which of
   * them it is standing in for.
   */
  const pendingDelete = ((): { subtitle: string; confirm: () => Promise<void> } | null => {
    if (deleting === null) return null;

    // The conditions a deletion takes with it are stripped on the way out, so the dialog
    // says how many rather than letting them vanish quietly.
    const andItsRules = (fields: PoiField[]) => {
      const count = fields.reduce((total, field) => total + rulesReferencing(field.id, shown).length, 0);

      return count ? ` ${t('Customization_ConfirmDeleteFieldRules', { count })}` : '';
    };

    if (deleting.kind === 'field') {
      const { group, index } = deleting;
      const field = fieldsOf(group)[index];

      return {
        subtitle: t('Customization_ConfirmDeleteField', [resolveLabel(field.label, locales)])
          + andItsRules([field]),
        confirm: () => saveType(withFields(shown, group, fieldsOf(group).filter((_, i) => i !== index))),
      };
    }

    if (deleting.kind === 'group') {
      const { index } = deleting;

      return {
        subtitle: t('Customization_ConfirmDeleteGroup', [resolveLabel(shown.groups[index].name, locales)])
          + andItsRules(shown.groups[index].fields),
        confirm: () => saveType({ groups: shown.groups.filter((_, i) => i !== index) }),
      };
    }

    return {
      subtitle: t('Customization_ConfirmDeleteType', [resolveLabel(poiType.name, locales)]),
      confirm: async () => {
        const result = await deletePoiType(poiType._id);

        if (result.error) return;

        // A draft of a type that is gone can be saved nowhere, and would still be asked about
        // before a reload.
        keptDraftsOf('poi-type').drop(userId, poiType._id);
        backToList();
      },
    };
  })();

  /**
   * A duplicate is a fresh create carrying the same content: only the primary locale of
   * the name is marked, so the copy is recognisable without inventing translations for
   * languages the author has not filled in.
   */
  const handleDuplicate = async () => {
    const result = await addPoiType({
      teamId: poiType.teamId,
      ...toPoiTypeBody({
        name: {
          ...poiType.name,
          [locales.primary]: `${resolveLabel(poiType.name, locales)} ${t('Customization_CopySuffix')}`,
        },
        description: poiType.description,
        inheritFrom: poiType.inheritFrom,
        fields: poiType.fields,
        groups: poiType.groups,
      }),
    });

    if (!result.error && 'data' in result && result.data) {
      navigate(generatePath(ROUTE_PATHS.CUSTOMIZATIONS_POI_TYPE, { poiTypeId: result.data.id }));
    }
  };

  /**
   * Header actions are icons here, the same shape the team detail header uses. The tooltip
   * names the wrapper a disabled button needs to show one at all, not the button, so the
   * button is named itself: by what it does, whatever the tooltip says.
   */
  const action = (
    title: string, allowed: boolean, icon: React.ReactNode, onClick: () => void, danger = false
  ) => (
    <Tooltip title={allowed ? title : t('Permission_NotAllowed')} arrow>
      <span>
        <IconButton
          aria-label={title}
          onClick={onClick}
          disabled={!allowed}
          sx={danger ? dangerHoverStyles : { color: 'text.secondary' }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Header />

      <Box sx={{ width: '100%' }}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box sx={detailIconStyles}>
              <MapMarkerIcon />
            </Box>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{resolveLabel(poiType.name, locales)}</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>{poiType.description}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                {t('Customization_Group_Count', { count: poiType.groups.length })} · {t('Customization_Field_Count', { count: fieldCount })}
                {` · v${poiType.version}`}
              </Typography>
            </Box>

            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
              {action(t('Common_Edit'), canEdit, <EditIcon />, () => setDrawer({ kind: 'type' }))}
              {action(t('Customization_Duplicate'), canEdit, <CopyIcon />, () => void handleDuplicate())}
              {action(t('Customization_ShareType'), canShare, <ShareAltIcon />, () => setDrawer({ kind: 'share' }))}
              {action(t('Common_Delete'), canDelete, <TrashIcon />, () => setDeleting({ kind: 'type' }), true)}
            </Stack>
          </Stack>
        </Paper>

        <ViewSwitch
          view={view}
          trying={trying}
          onView={(next) => {
            setView(next);
            setTrying(false);
          }}
          onTry={setTrying}
        />

        {/* Kept mounted behind the table, so unsaved designer edits survive a look at it. */}
        <Box sx={{ display: view === 'table' && !trying ? 'none' : 'block' }}>
          <PoiTypeDesigner
            // A different type starts a fresh draft.
            key={poiType._id}
            poiType={shown}
            mode={trying ? 'try' : view === 'json' ? 'json' : 'design'}
            effective={effective}
            canEdit={canEdit}
            saving={saving}
            onSave={async (draft, version) => {
              const result = await enqueue(() => sendType(draft, version));
              const failure = { error: result.error };

              if (!result.error) return { ok: true };

              // The snackbar shows the refusal too; the JSON view also shows it beside the text.
              return { ok: false, message: isServerError(failure) ? apiErrorMessage(failure.error.data) : undefined };
            }}
          />
        </Box>

        {view === 'table' && !trying && (
          <PoiTypeSchema
            poiType={shown}
            parentName={parent && resolveLabel(parent.name, locales)}
            onOpenParent={parent
              ? () => navigate(generatePath(ROUTE_PATHS.CUSTOMIZATIONS_POI_TYPE, { poiTypeId: parent._id }))
              : undefined}
            onAddField={(group) => setDrawer({ kind: 'field', group, index: null })}
            onEditField={(group, index) => setDrawer({ kind: 'field', group, index })}
            onDeleteField={(group, index) => setDeleting({ kind: 'field', group, index })}
            onReorderField={handleReorderField}
            onAddGroup={() => setDrawer({ kind: 'group', index: null })}
            onEditGroup={(index) => setDrawer({ kind: 'group', index })}
            onDeleteGroup={(index) => setDeleting({ kind: 'group', index })}
            onReorderGroup={handleReorderGroup}
          />
        )}
      </Box>

      <PoiTypeDrawer
        open={drawer.kind === 'type'}
        poiType={poiType}
        parentCandidates={poiTypes.filter((candidate) => !excludedParentIds.has(candidate._id))}
        teams={[]}
        saving={saving}
        onClose={close}
        onSave={handleSaveType}
      />

      <GroupDrawer
        open={drawer.kind === 'group'}
        group={drawer.kind === 'group' && drawer.index !== null ? shown.groups[drawer.index] : null}
        candidates={ruleCandidates}
        typeName={resolveLabel(poiType.name, locales)}
        saving={saving}
        onClose={close}
        onSave={handleSaveGroup}
      />

      <FieldDrawer
        open={drawer.kind === 'field'}
        field={drawer.kind === 'field' && drawer.index !== null ? fieldsOf(drawer.group)[drawer.index] : null}
        group={drawer.kind === 'field' ? drawer.group : 'd'}
        groupNames={shown.groups.map((group) => resolveLabel(group.name, locales))}
        candidates={ruleCandidates}
        typeName={resolveLabel(poiType.name, locales)}
        saving={saving}
        onClose={close}
        onSave={handleSaveField}
      />

      <DeleteConfirmationDialog
        open={pendingDelete !== null}
        warningSubtitle={pendingDelete?.subtitle}
        onClose={() => setDeleting(null)}
        onConfirm={() => pendingDelete?.confirm() ?? Promise.resolve()}
      />

      <ShareDrawer
        open={drawer.kind === 'share'}
        poiType={poiType}
        teams={teamDirectory}
        saving={isSharing || isRevoking}
        onClose={close}
        onShare={(teamId) => sharePoiType({ id: poiType._id, teamIds: [teamId] })}
        onRevoke={(teamId) => deletePoiTypeShare({ id: poiType._id, teamId })}
      />
    </Box>
  );
};

export default PoiTypeDetailPage;
